/**
 * Browser OpenTelemetry bootstrap: tracer provider, exporter, and
 * instrumentation registration.
 *
 * Mirrors src/auth/entra-browser.ts's shape — a pure `resolveConfig(env)`
 * that returns `undefined` when unconfigured (telemetry is off by default,
 * the same pattern Entra uses), plus an explicit `init()` the composition
 * root calls once.
 *
 * Deliberately skips `@opentelemetry/context-zone`: it needs `zone.js`,
 * which monkey-patches global `setTimeout`/`Promise`/event listeners to
 * propagate context across async boundaries. This app already has two
 * documented timing sensitivities a global patch is likely to interact badly
 * with — React 19 `<StrictMode>` double-invoking effects, and MSAL's
 * `initialize()` promise-caching race (see AGENTS.md: "sign-in getting stuck
 * on 'Signing in' with zero network activity"). `StackContextManager` (no
 * global patching) is used instead; the trade-off is that context does not
 * automatically survive a true async gap, so code that needs it re-enters
 * context explicitly with `context.with()` — see `useSpan` in
 * src/telemetry/use-span.ts.
 *
 * @module telemetry/tracer-provider
 */
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { UserInteractionInstrumentation } from "@opentelemetry/instrumentation-user-interaction";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  type SpanProcessor,
  StackContextManager,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from "@opentelemetry/semantic-conventions";

import { PrivacySanitizingSpanProcessor } from "./privacy-span-processor";
import { TenantContextSpanProcessor } from "./tenant-context-span-processor";
import { UserContextSpanProcessor } from "./user-context-span-processor";

/** Resolved browser telemetry configuration. */
export interface TelemetryBrowserConfig {
  /** OTel `service.name` resource attribute. */
  serviceName: string;
  /** OTel `service.version` resource attribute. */
  serviceVersion: string;
  /** Same-origin path the OTLP/HTTP exporter posts to (proxied to the collector). */
  tracesEndpoint: string;
  /**
   * Fraction of traces sampled in, [0, 1]. Volume/cost control only — never a
   * privacy control. Sanitization in {@link PrivacySanitizingSpanProcessor}
   * runs on every span regardless of whether it was sampled in.
   */
  samplingRatio: number;
}

/**
 * `service.name` when `VITE_OTEL_SERVICE_NAME` is unset. Telemetry carries its
 * own default rather than requiring one, so a deployment gets traces without
 * having to name the service first.
 */
const DEFAULT_SERVICE_NAME = "socratiq-web";

/**
 * Resolve browser telemetry configuration from an environment record
 * (typically `import.meta.env`). Telemetry is on unless `VITE_OTEL_ENABLED` is
 * exactly `"false"`, which returns `undefined` and the composition root reads
 * as "skip initialization entirely". This is the same "on unless explicitly
 * false" shape `VITE_ENTRA_MULTI_TENANT` uses, rather than Entra's
 * "unconfigured = off": sign-in cannot work without real credentials, whereas
 * tracing has a working default for every field.
 *
 * @param env - Environment variables, e.g. `import.meta.env`.
 * @param appVersion - The application's version, used as `service.version`.
 * @returns The resolved configuration, or `undefined` when telemetry is disabled.
 */
export function resolveTelemetryBrowserConfig(
  env: Record<string, string | undefined>,
  appVersion: string,
): TelemetryBrowserConfig | undefined {
  if (env.VITE_OTEL_ENABLED?.trim() === "false") {
    return undefined;
  }

  const serviceName = env.VITE_OTEL_SERVICE_NAME?.trim() || DEFAULT_SERVICE_NAME;

  // Default 1 (no sampling): this app's traffic is departmental, not mass
  // market (tens of users per institution, a subset of a 40-60 person
  // department), so ingestion volume at full sampling is not expected to
  // approach Application Insights/Log Analytics cost thresholds. Lower this
  // deliberately if usage ever grows enough that it does, not preemptively.
  //
  // `?.trim()` before falling back, not `??`: the Dockerfile's ARG default
  // for this is an empty string, not unset, and `"" ?? "1"` would still be
  // `""` (`??` only falls through on null/undefined). `Number("")` is `0`,
  // which would silently sample out every trace on a deployment that never
  // sets this at all — the default case, now that telemetry is on by default.
  const rawRatioInput = env.VITE_OTEL_SAMPLING_RATIO?.trim();
  const rawRatio = rawRatioInput ? Number(rawRatioInput) : 1;
  const samplingRatio = Number.isFinite(rawRatio) ? Math.min(1, Math.max(0, rawRatio)) : 1;

  return {
    serviceName,
    serviceVersion: appVersion,
    tracesEndpoint: env.VITE_OTEL_TRACES_ENDPOINT?.trim() || "/api/v1/telemetry/v1/traces",
    samplingRatio,
  };
}

/**
 * Initialize the browser tracer provider: user-context and tenant-context
 * enrichment, then privacy sanitization, then batch export (registration
 * order matters — see {@link PrivacySanitizingSpanProcessor}), with
 * document-load, same-origin fetch, and click-interaction instrumentation
 * registered against it.
 *
 * @param config - The resolved telemetry configuration.
 * @returns The initialized, registered tracer provider.
 */
export function initBrowserTelemetry(config: TelemetryBrowserConfig): WebTracerProvider {
  const exporter = new OTLPTraceExporter({ url: config.tracesEndpoint });

  const processors: SpanProcessor[] = [
    new UserContextSpanProcessor(),
    new TenantContextSpanProcessor(),
    new PrivacySanitizingSpanProcessor(),
    new BatchSpanProcessor(exporter),
  ];

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: config.serviceName,
      [ATTR_SERVICE_VERSION]: config.serviceVersion,
    }),
    spanProcessors: processors,
    // Volume/cost control only (checklist B4) — sanitization above runs on
    // every span regardless of what this sampler decides.
    sampler: new ParentBasedSampler({ root: new TraceIdRatioBasedSampler(config.samplingRatio) }),
  });

  provider.register({ contextManager: new StackContextManager() });

  registerInstrumentations({
    tracerProvider: provider,
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        // Only same-origin API calls are worth correlating; Entra's own token
        // endpoints sit on a different origin and their URLs can carry
        // short-lived auth codes, so they are excluded outright rather than
        // sanitized after the fact.
        ignoreUrls: [/login\.microsoftonline\.com/, /ciamlogin\.com/],
        propagateTraceHeaderCorsUrls: typeof window !== "undefined" ? [window.location.origin] : [],
      }),
      // Audited before enabling: every dynamic DOM `id` in src/components and
      // src/reports/components is built from opaque uid()-generated entity
      // ids or fixed field-name strings, never from an individual's personal
      // data (an email address, chiefly) — see the browser telemetry
      // implementation spec's user-interaction section. That matters because
      // this instrumentation's `target_xpath` attribute embeds an element's
      // raw `id` attribute verbatim when one is present
      // (getElementXPath(..., true) in @opentelemetry/sdk-trace-web). Default
      // eventNames (click only) — no reason yet to widen it.
      new UserInteractionInstrumentation(),
    ],
  });

  return provider;
}
